<template>
  <component :is="embedded ? 'div' : 'v-main'">
    <component :is="embedded ? 'div' : 'v-container'" v-bind="embedded ? {} : { fluid: true }">
      <v-card :elevation="embedded ? 0 : 6" :rounded="embedded ? undefined : 'xxl'">
        <!-- Embedded (dialog) use gets its banner header from AppDialog instead. -->
        <v-card-title v-if="!embedded">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark>mdi-arrow-left</v-icon> </v-btn
          >Add New Vendor
        </v-card-title>
        <v-card-text>
          <!-- Basic Information -->
          <AppSectionHeader label="Basic Information" />
          <v-row>
            <v-col sm="6">
              <v-text-field
                v-model="form.name"
                outlined
                placeholder="e.g. Vendor Name"
                :error-messages="errors.name"
              >
                <template v-slot:label>Vendor Name <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.contact_person"
                outlined
                placeholder="e.g. Contact Person"
                :error-messages="errors.contact_person"
              >
                <template v-slot:label>Contact Person <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.phone"
                outlined
                placeholder="e.g. 9876543210"
                hint="10-digit mobile number"
                persistent-hint
                :error-messages="errors.phone"
              >
                <template v-slot:label>Phone <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.email"
                outlined
                placeholder="e.g. contact@vendor.com"
                hint="Valid email address"
                persistent-hint
                :error-messages="errors.email"
              >
                <template v-slot:label>Email <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
          </v-row>

          <!-- Address -->
          <AppSectionHeader label="Address" />
          <v-row>
            <v-col sm="12">
              <v-textarea
                v-model="form.address"
                outlined
                rows="2"
                auto-grow
                :error-messages="errors.address"
              >
                <template v-slot:label>Full Address <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-textarea>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.city"
                outlined
                :error-messages="errors.city"
              >
                <template v-slot:label>City <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.state"
                outlined
                :error-messages="errors.state"
              >
                <template v-slot:label>State <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
            <v-col sm="4">
              <v-text-field
                v-model="form.pin_code"
                outlined
                :error-messages="errors.pin_code"
              >
                <template v-slot:label>Pincode <span style="color:#f44336;font-weight:bold;">*</span></template>
              </v-text-field>
            </v-col>
          </v-row>

          <!-- Tax Information -->
          <AppSectionHeader label="Tax Information" />
          <v-row>
            <v-col sm="6">
              <v-text-field
                v-model="form.gstin"
                outlined
                label="GST Number"
                placeholder="e.g. 22AAAAA0000A1Z5"
                hint="Format: 2 digits + 10-char PAN + 1 digit + Z + 1 char"
                persistent-hint
                @input="form.gstin = form.gstin ? form.gstin.toUpperCase() : form.gstin"
                :error-messages="errors.gstin"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.pan_number"
                outlined
                label="PAN Number"
                placeholder="e.g. ABCDE1234F"
                hint="Format: 5 letters + 4 digits + 1 letter (all uppercase)"
                persistent-hint
                @input="form.pan_number = form.pan_number ? form.pan_number.toUpperCase() : form.pan_number"
                :error-messages="errors.pan_number"
              ></v-text-field>
            </v-col>
          </v-row>

          <!-- Bank Details -->
          <AppSectionHeader label="Bank Details" />
          <v-row>
            <v-col sm="6">
              <v-text-field
                v-model="form.bank_name"
                outlined
                label="Bank Name"
                :error-messages="errors.bank_name"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.branch_name"
                outlined
                label="Branch Name"
                :error-messages="errors.branch_name"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.bank_account_number"
                outlined
                label="Account Number"
                placeholder="e.g. 1234567890123456"
                hint="9 to 18 digit bank account number"
                persistent-hint
                :error-messages="errors.bank_account_number"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.bank_ifsc"
                outlined
                label="IFSC Code"
                placeholder="e.g. SBIN0001234"
                hint="Format: 4 letters + 0 + 6 alphanumeric characters"
                persistent-hint
                @input="form.bank_ifsc = form.bank_ifsc ? form.bank_ifsc.toUpperCase() : form.bank_ifsc"
                :error-messages="errors.bank_ifsc"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.bank_account_name"
                outlined
                label="Account Holder Name"
                :error-messages="errors.bank_account_name"
              ></v-text-field>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="12">
              <v-textarea
                v-model="form.remarks"
                outlined
                label="Remarks"
                rows="2"
                auto-grow
                :error-messages="errors.remarks"
              ></v-textarea>
            </v-col>
          </v-row>

          <!-- Status -->
          <AppSectionHeader label="Status" />
          <v-row align="center">
            <v-col sm="8">
              <span class="text-caption grey--text">Set vendor status</span>
            </v-col>
            <v-col sm="4" class="text-right">
              <v-switch
                class="mt-0 d-inline-flex"
                v-model="form.is_active"
                inset
                :label="form.is_active ? 'Active' : 'Inactive'"
              ></v-switch>
            </v-col>
          </v-row>

          <v-divider class="mt-5 mb-5"> </v-divider>
          <div class="text-right">
            <v-btn text class="mr-2" @click="embedded ? $emit('close') : $router.back()">Cancel</v-btn>
            <v-btn
              :loading="isLoading"
              :disabled="isLoading"
              rounded
              color="primary"
              dark
              @click="save"
            >
              Create
            </v-btn>
          </div>
        </v-card-text>
      </v-card>
    </component>
  </component>
</template>
<script>
import axios from "axios";
import AppSectionHeader from "@/components/AppSectionHeader.vue";
export default {
  name: "VendorCreatePage",
  components: {
    AppSectionHeader,
  },
  props: {
    // When true, this form is rendered inside another page's dialog (e.g.
    // the Purchase Order create screen's inline "Create Vendor") instead of
    // its own routed page - it emits `created`/`close` rather than
    // navigating away, and drops its own page-level layout wrapper.
    embedded: { type: Boolean, default: false },
    // Name typed into the picker before choosing "Create" - pre-fills Vendor Name.
    initialName: { type: String, default: "" },
  },
  data() {
    return {
      form: {
        is_active: true,
        name: this.initialName ? this.initialName.trim() : undefined,
      },
      isLoading: false,
    };
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
  },
  mounted() {
    this.$store.dispatch("errors/setErrors", {});
  },
  methods: {
    async save() {
      try {
        this.isLoading = true;
        this.$store.dispatch("errors/setErrors", {});
        const response = await axios.post("/vendors", this.form);
        this.isLoading = false;
        if (this.embedded) {
          this.$emit("created", response.data.data);
        } else {
          this.$router.push("/vendors");
        }
      } catch (e) {
        console.log(e);
        this.isLoading = false;
        if (e.response && e.response.data && e.response.data.errors) {
          this.$store.dispatch("errors/setErrors", e.response.data.errors);
        }
      }
    },
  },
};
</script>
